import { useState } from 'react';
import { useTranslation } from 'react-i18next';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';

// Dismissing hides the banner for this long, then it reappears if the profile is
// still incomplete -- long enough not to be annoying within a session, short enough
// that an incomplete profile doesn't stay silently dismissed forever.
const DISMISS_WINDOW_MS = 24 * 60 * 60 * 1000;

// Shown at the top of the Dashboard tab for Employer/Union/Organization accounts
// while their profile-completion checklist (already computed by each dashboard) has
// missing items. Each dashboard owns its own checklist and field labels -- this
// component just renders whatever it's given and persists the dismissal.
export default function ProfileCompletionBanner({ storageKey, profileLabel, missingItems, pct, onNavigate }) {
  const { t } = useTranslation();
  const [dismissed, setDismissed] = useState(() => {
    const raw = localStorage.getItem(storageKey);
    if (!raw) return false;
    return Date.now() - Number(raw) < DISMISS_WINDOW_MS;
  });

  if (!missingItems || missingItems.length === 0 || dismissed) return null;

  const handleDismiss = () => {
    localStorage.setItem(storageKey, String(Date.now()));
    setDismissed(true);
  };

  return (
    <div style={{
      backgroundColor: NAVY_MID, border: `1px solid rgba(245,166,35,0.35)`, borderLeft: `3px solid ${AMBER}`,
      borderRadius: '10px', padding: '14px 16px', marginBottom: '20px',
      display: 'flex', alignItems: 'flex-start', gap: '12px',
    }}>
      <div style={{ flexShrink: 0, marginTop: '1px' }}>
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={AMBER} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12.5" />
          <circle cx="12" cy="16" r="0.5" fill={AMBER} />
        </svg>
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <p style={{ fontSize: '0.875rem', color: WHITE, fontWeight: 700, marginBottom: '4px' }}>
          {t('common.profileCompletionBanner.yourXIsPctComplete', { profileLabel, pct })}
        </p>
        <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.65)', lineHeight: 1.5, marginBottom: '10px' }}>
          {t('common.profileCompletionBanner.missingPrefix')} {missingItems.map(i => i.label).join(', ')}
        </p>
        <button
          onClick={() => onNavigate(missingItems[0].tab)}
          style={{ backgroundColor: AMBER, color: NAVY, border: 'none', borderRadius: '8px', padding: '8px 16px', fontWeight: 700, fontSize: '0.8rem', cursor: 'pointer' }}
        >
          {t('common.profileCompletionBanner.completeProfile')}
        </button>
      </div>
      <button
        onClick={handleDismiss}
        aria-label={t('common.profileCompletionBanner.dismissAriaLabel')}
        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.4)', fontSize: '1.3rem', lineHeight: 1, padding: '2px', flexShrink: 0 }}
      >
        ×
      </button>
    </div>
  );
}
